import { useRef, useMemo, useState, useEffect, useCallback } from 'react';

import axios, { endpoints } from 'src/utils/axios';

// ----------------------------------------------------------------------

const ASSISTANT_ID = 'assistant';
const ASSISTANT_NAME = 'oneVIEW Insights';

function parseJson(value, fallback) {
  if (typeof value !== 'string') return fallback;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function getCurrentUser() {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return {
      id: 'me',
      name: 'Me',
      avatarUrl: '',
      email: '',
      role: 'admin',
    };
  }

  const parsed = parseJson(window.localStorage.getItem('user') || 'null', null);
  const id = `${parsed?.user_id ?? parsed?.id ?? 'me'}`;

  const displayName = parsed?.first_name
    ? `${parsed.first_name} ${parsed.last_name ?? ''}`.trim()
    : parsed?.name ?? parsed?.username ?? 'User';

  return {
    id,
    name: displayName,
    avatarUrl: parsed?.photoURL ?? '',
    email: parsed?.email ?? '',
    role: parsed?.roles?.[0] ?? 'admin',
  };
}

function buildParticipants(updatedAt) {
  const user = getCurrentUser();
  return [
    {
      id: ASSISTANT_ID,
      name: ASSISTANT_NAME,
      avatarUrl: '',
      status: 'online',
      role: 'assistant',
      email: '',
      lastActivity: updatedAt,
    },
    {
      id: user.id,
      name: user.name,
      avatarUrl: user.avatarUrl,
      status: 'online',
      role: `${user.role}`,
      email: user.email,
      lastActivity: updatedAt,
    },
  ];
}

function normalizeConversation(conversation) {
  const insertedAt =
    conversation?.rowDateInserted || conversation?.row_date_inserted || new Date().toISOString();
  return {
    id: `${conversation?.id}`,
    subject: conversation?.subject || 'New Conversation',
    conversationType: conversation?.conversationType || conversation?.conversation_type || 'global',
    participants: buildParticipants(insertedAt),
    usage: {
      promptTokens: 0,
      completionTokens: 0,
      totalTokens: 0,
    },
    createdAt: insertedAt,
    updatedAt: insertedAt,
    messages: (conversation?.messages || []).map((m) => ({
      ...m,
      senderId: `${m?.senderId ?? m?.sender_id ?? ''}`,
      contentType: m?.contentType || m?.content_type || 'text',
      createdAt: m?.rowDateInserted || m?.row_date_inserted || insertedAt,
    })),
  };
}

function createEphemeralConversation(initialSubject = 'New chat', conversationType = 'global') {
  const now = new Date().toISOString();
  const id = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `conv-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  return {
    id,
    subject: initialSubject,
    conversationType,
    participants: buildParticipants(now),
    usage: {
      promptTokens: 0,
      completionTokens: 0,
      totalTokens: 0,
    },
    createdAt: now,
    updatedAt: now,
    messages: [],
    isEphemeral: true,
  };
}

// ----------------------------------------------------------------------

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function useChatStore({ conversationType } = {}) {
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [conversationsLoaded, setConversationsLoaded] = useState(false);
  const [conversationLoading, setConversationLoading] = useState(false);

  const conversationsRef = useRef(conversations);
  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  useEffect(() => {
    if (!conversationsLoaded) return;
    if (!activeConversationId || !UUID_RE.test(activeConversationId)) {
      setConversationLoading(false);
      return;
    }

    const activeConv = conversationsRef.current.find((c) => c.id === activeConversationId);
    if (activeConv?.isEphemeral) {
      setConversationLoading(false);
      return;
    }

    let mounted = true;
    setConversationLoading(true);

    axios
      .get(endpoints.knowledge.conversationDetails(activeConversationId))
      .then(({ data }) => {
        if (!mounted) return;
        const normalized = normalizeConversation(data);
        setConversations((prev) => {
          return prev.map((conv) =>
            conv.id === activeConversationId
              ? { ...normalized, usage: conv.usage }
              : conv
          );
        });
      })
      .catch((err) => {
        console.error('[chat] Failed to fetch conversation details', err);
      })
      .finally(() => {
        if (mounted) {
          setConversationLoading(false);
        }
      });

    axios
      .get(endpoints.knowledge.usageConversation(activeConversationId))
      .then(({ data }) => {
        if (!mounted) return;
        const totals = data?.totals || {};
        const promptTokens = Number(totals.promptTokens ?? totals.prompt_tokens ?? 0) || 0;
        const completionTokens = Number(totals.completionTokens ?? totals.completion_tokens ?? 0) || 0;
        const totalTokens = Number(totals.totalTokens ?? totals.total_tokens ?? 0) || 0;

        setConversations((prev) =>
          prev.map((conv) => {
            if (conv.id !== activeConversationId) return conv;
            // Only apply if the server total is higher than what we already have
            // (so we never overwrite tokens accumulated in the current session).
            if (totalTokens <= conv.usage.totalTokens) return conv;
            return {
              ...conv,
              usage: { promptTokens, completionTokens, totalTokens },
            };
          })
        );
      })
      .catch(() => {});

    return () => {
      mounted = false;
    };
  }, [activeConversationId, conversationsLoaded]);

  useEffect(() => {
    let mounted = true;

    // The widget stays mounted while switching between global and report insights, so drop the
    // previous type's conversations/selection; otherwise the next message is sent into (and
    // merged with) the conversation that was active under the other type.
    setActiveConversationId(null);
    setConversations([]);
    setConversationsLoaded(false);

    const fetchConversations = async () => {
      try {
        const { data } = await axios.get(endpoints.knowledge.conversations(conversationType));
        if (!mounted) return;
        const normalized = (data?.results || []).map(normalizeConversation);
        setConversations(normalized);
        setConversationsLoaded(true);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[chat] Failed to fetch conversations', error);
      }
    };

    fetchConversations();
    return () => {
      mounted = false;
    };
  }, [conversationType]);

  const activeConversation = useMemo(
    () => conversations.find((c) => c.id === activeConversationId) || null,
    [conversations, activeConversationId]
  );

  const setActiveConversation = useCallback((id) => {
    setActiveConversationId((prevId) => {
      if (prevId === id) return prevId;
      if (id && UUID_RE.test(id)) {
        const activeConv = conversationsRef.current.find((c) => c.id === id);
        if (!activeConv?.isEphemeral) {
          setConversationLoading(true);
        }
      }
      return id;
    });
  }, []);

  const ensureConversation = useCallback(
    (subject) => {
      if (activeConversation) return activeConversation;
      const next = createEphemeralConversation(subject, conversationType || 'global');
      setConversations((prev) => [...prev, next]);
      setActiveConversationId(next.id);
      return next;
    },
    [activeConversation, conversationType]
  );

  const createConversationWithSubject = useCallback(
    (subject) => {
      const next = createEphemeralConversation(subject, conversationType || 'global');
      setConversations((prev) => [...prev, next]);
      setActiveConversationId(next.id);
      return next;
    },
    [conversationType]
  );

  const appendMessages = useCallback(
    (messages) => {
      const toAppend = Array.isArray(messages) ? messages : [messages];
      if (!toAppend.length || !activeConversationId) return;

      setConversations((prev) =>
        prev.map((conv) => {
          if (conv.id !== activeConversationId) return conv;
          const nextMessages = [...conv.messages, ...toAppend];
          const lastTimestamp = toAppend[toAppend.length - 1]?.createdAt || new Date().toISOString();
          return {
            ...conv,
            messages: nextMessages,
            updatedAt: lastTimestamp,
          };
        })
      );
    },
    [activeConversationId]
  );

  const updateMessageById = useCallback(
    (messageId, updater) => {
      if (!activeConversationId || !messageId || typeof updater !== 'function') return;

      setConversations((prev) =>
        prev.map((conv) => {
          if (conv.id !== activeConversationId) return conv;
          let updated = false;
          const nextMessages = conv.messages.map((msg) => {
            if (msg.id !== messageId) return msg;
            updated = true;
            return updater(msg);
          });
          if (!updated) return conv;
          const lastTimestamp = nextMessages[nextMessages.length - 1]?.createdAt || new Date().toISOString();
          return {
            ...conv,
            messages: nextMessages,
            updatedAt: lastTimestamp,
          };
        })
      );
    },
    [activeConversationId]
  );

  const updateSubject = useCallback(
    (subject) => {
      if (!activeConversationId) return;
      const trimmed = subject?.trim() || 'New chat';
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === activeConversationId
            ? {
                ...conv,
                subject: trimmed,
                updatedAt: new Date().toISOString(),
              }
            : conv
        )
      );
    },
    [activeConversationId]
  );

  const upsertConversationFromServer = useCallback((serverConversation) => {
    if (!serverConversation) return;
    const normalized = normalizeConversation(serverConversation);
    setConversations((prev) => {
      const existing = prev.find((conv) => `${conv.id}` === `${normalized.id}`);
      if (!existing) return [normalized, ...prev];
      return prev.map((conv) =>
        `${conv.id}` === `${normalized.id}`
          ? {
              ...normalized,
              // Keep the type the chat was opened with, so an answer never flips the header
              // between Report and Global Insights.
              conversationType: conv.conversationType || normalized.conversationType,
              usage: conv.usage,
            }
          : conv
      );
    });
    setActiveConversationId(`${normalized.id}`);
  }, []);

  const updateUsage = useCallback(
    (rawUsage) => {
      if (!activeConversationId || !rawUsage) return;

      const prompt =
        Number(rawUsage.promptTokens ?? rawUsage.prompt_tokens ?? 0) || 0;
      const completion =
        Number(rawUsage.completionTokens ?? rawUsage.completion_tokens ?? 0) || 0;
      const total =
        Number(rawUsage.totalTokens ?? rawUsage.total_tokens ?? prompt + completion) || 0;

      setConversations((prev) =>
        prev.map((conv) => {
          if (conv.id !== activeConversationId) return conv;
          const current = conv.usage || {
            promptTokens: 0,
            completionTokens: 0,
            totalTokens: 0,
          };
          return {
            ...conv,
            usage: {
              promptTokens: current.promptTokens + prompt,
              completionTokens: current.completionTokens + completion,
              totalTokens: current.totalTokens + total,
            },
            updatedAt: new Date().toISOString(),
          };
        })
      );
    },
    [activeConversationId]
  );

  const resetConversation = useCallback(
    (id) => {
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === (id || activeConversationId)
            ? {
                ...conv,
                messages: [],
                updatedAt: new Date().toISOString(),
              }
            : conv
        )
      );
    },
    [activeConversationId]
  );

  const deleteConversation = useCallback(
    (id) => {
      const targetId = id || activeConversationId;
      if (!targetId) return;

      setConversations((prev) => {
        const next = prev.filter((conv) => conv.id !== targetId);
        if (next.length === prev.length) {
          return prev;
        }

        setActiveConversationId((currentActiveId) => {
          if (currentActiveId !== targetId) {
            return currentActiveId;
          }
          if (next.length === 0) {
            return null;
          }
          return next[0].id;
        });

        return next;
      });
    },
    [activeConversationId]
  );

  return {
    conversations,
    conversationsLoaded,
    conversationLoading,
    activeConversation,
    activeConversationId,
    setActiveConversation,
    ensureConversation,
    createConversation: createConversationWithSubject,
    appendMessages,
    updateMessageById,
    updateSubject,
    updateUsage,
    upsertConversationFromServer,
    resetConversation,
    deleteConversation,
  };
}
